import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { appConfigApi, featureConfigKey, isFeatureEnabled } from '@/api/appConfig';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { humanizeError } from '@/lib/errors';

import { AiSkillsServerCard } from './AiSkillsServerCard';
import { AppVersionsCard } from './AppVersionsCard';
import { RecordVoiceCard } from './RecordVoiceCard';

/**
 * Управление приложением: что показывать ученикам, задаётся отсюда.
 *
 * ⚠️ ЗАЧЕМ СТРАНИЦА. Раньше, чтобы убрать из приложения одну возможность,
 * требовалась новая сборка и новая проверка в App Store — неделя ожидания.
 * Здесь тумблер применяется сразу и ко всем ученикам.
 *
 * ⚠️ ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Никаких платёжных настроек: тумблер, способный
 * показать оплату после одобрения проверки, — это правило Apple 2.3.1, за него
 * закрывают аккаунт разработчика целиком. Платёжные экраны закрыты в коде и
 * настройкой не открываются.
 */

/** Возможности в том порядке, в каком их видит ученик. */
const FEATURES = [
  'lessons',
  'vocab',
  'cefr',
  'ielts',
  // Отдельные тренажёры: каждый гасится своим тумблером, не все разом.
  'ielts_reading',
  'ielts_listening',
  'ielts_writing',
  'ielts_speaking',
  'mock',
  'sprint',
  'speaking_ai',
  'writing_ai',
  'chat',
  'attendance',
  'live',
  'speaking_peers',
  // Главная страница сайта до входа. В приложении не выключается — см. appConfig.
  'landing',
] as const;

export function AdminAppConfigPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const config = useQuery({
    queryKey: ['admin', 'app-config'],
    queryFn: appConfigApi.get,
  });

  const toggle = useMutation({
    mutationFn: ({ feature, next }: { feature: string; next: boolean }) =>
      appConfigApi.set(featureConfigKey(feature), next),
    // Сервер отвечает целым документом — кладём его как новую истину, иначе две
    // открытые вкладки разошлись бы с сервером и друг с другом.
    onSuccess: (doc) => qc.setQueryData(['admin', 'app-config'], doc),
  });

  const doc = config.data;

  return (
    <div className="space-y-6">
      <PageHeader title={t('adminAppConfig.title')} subtitle={t('adminAppConfig.subtitle')} />

      <Card>
        <p className="mb-4 text-sm text-on-surface-variant">{t('adminAppConfig.featuresNote')}</p>

        {config.isLoading && <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>}
        {config.isError && (
          <p className="text-sm text-error">{humanizeError(config.error)}</p>
        )}

        <ul className="divide-y divide-outline-variant">
          {FEATURES.map((feature) => {
            const on = isFeatureEnabled(doc, feature);
            const busy = toggle.isPending && toggle.variables?.feature === feature;
            return (
              <li key={feature} className="flex items-center justify-between gap-4 py-3">
                <span className="text-sm text-on-surface">{t(`tariff.feature${featureLabelKey(feature)}`)}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={t(`tariff.feature${featureLabelKey(feature)}`)}
                  disabled={busy || config.isLoading}
                  onClick={() => toggle.mutate({ feature, next: !on })}
                  className={[
                    'relative h-6 w-11 flex-none rounded-full transition-colors',
                    on ? 'bg-primary' : 'bg-outline-variant',
                    busy ? 'opacity-50' : '',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-all',
                      on ? 'left-[calc(100%-1.375rem)]' : 'left-0.5',
                    ].join(' ')}
                  />
                </button>
              </li>
            );
          })}
        </ul>

        {toggle.isError && (
          <p className="mt-3 text-sm text-error">{humanizeError(toggle.error)}</p>
        )}
      </Card>

      {/* ИИ-задания по навыкам (12.10.2026, docs/CONTRACTS/ai-skills.md). */}
      <AiSkillsServerCard />

      {/* Версии приложения iOS: экран «Нужно обновить» и плашка «Вышла новая версия» (15.10.2026). */}
      <AppVersionsCard />

      {/* Запись голоса: номер у записи, хранение файлом, сжатие (промпт 22, 18.10.2026). */}
      <RecordVoiceCard />
    </div>
  );
}

/**
 * Подпись возможности берём из тех же переводов, что и тумблеры в тарифе:
 * два списка названий однажды разошлись бы, и администратор читал бы в одном
 * месте «Спикинг с ИИ», а в другом «Живой разговор».
 */
export function featureLabelKey(feature: string): string {
  const byFeature: Record<string, string> = {
    lessons: 'Lessons',
    live: 'Live',
    attendance: 'Attendance',
    speaking_peers: 'SpeakingPeers',
    landing: 'Landing',
    speaking_ai: 'SpeakingAI',
    chat: 'Chat',
    vocab: 'Vocab',
    cefr: 'Cefr',
    ielts: 'Ielts',
    ielts_reading: 'IeltsReading',
    ielts_listening: 'IeltsListening',
    ielts_writing: 'IeltsWriting',
    ielts_speaking: 'IeltsSpeaking',
    sprint: 'Sprint',
    mock: 'Mock',
    writing_ai: 'WritingAI',
  };
  return byFeature[feature] ?? feature;
}
